import React, { useState } from 'react';
import { MapPin, Briefcase, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export const Careers = () => {
  return (
    <div className="careers-page pt-20">
      {/* Hero Section */}
      <section className="careers-hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-white mb-6">
              Build Your Career<br />
              <span className="text-gradient">At RADIUS</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Join a team where strategy meets creativity, and every day brings new challenges worth solving.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="why-join py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              Why RADIUS?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="benefit-card hover-lift">
              <CardContent className="p-8">
                <div className="text-5xl mb-4">🚀</div>
                <h3 className="text-2xl font-bold text-white mb-4">High-Impact Work</h3>
                <p className="text-gray-400">
                  Work on challenging projects with top-tier clients. Your work will be seen, measured, and celebrated.
                </p>
              </CardContent>
            </Card>

            <Card className="benefit-card hover-lift">
              <CardContent className="p-8">
                <div className="text-5xl mb-4">🎯</div>
                <h3 className="text-2xl font-bold text-white mb-4">Learning & Growth</h3>
                <p className="text-gray-400">
                  From AI tools to emerging channels, you'll stay at the cutting edge. We invest in your development.
                </p>
              </CardContent>
            </Card>

            <Card className="benefit-card hover-lift">
              <CardContent className="p-8">
                <div className="text-5xl mb-4">⚖️</div>
                <h3 className="text-2xl font-bold text-white mb-4">Work-Life Balance</h3>
                <p className="text-gray-400">
                  We work hard, but sustainably. Flexible hours, remote options, and a culture that respects boundaries.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="open-positions py-24 bg-black">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              Open Positions
            </h2>
            <p className="text-xl text-gray-400">
              Find the role that matches your ambition
            </p>
          </div>

          <Card className="no-positions-card">
            <CardContent className="p-16 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-20 h-20 bg-red-600/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Briefcase className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  No Open Roles at Present
                </h3>
                <p className="text-gray-400 mb-8">
                  We don't have any open positions at the moment, but we're always looking for exceptional talent. Check back soon or send us your details for future opportunities.
                </p>
                <Button 
                  onClick={() => toast.success('We\'ll keep your information on file for future opportunities!')}
                  className="btn-primary group"
                >
                  Send General Application
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Culture */}
      <section className="culture py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-white mb-6">
                Our Culture
              </h2>
              <div className="space-y-4 text-gray-400 text-lg leading-relaxed">
                <p>
                  At RADIUS, we believe great work comes from great teams. We've built a culture where curiosity is rewarded, collaboration is the norm, and everyone's voice matters.
                </p>
                <p>
                  We're direct and transparent. We celebrate wins and learn from failures. We push each other to be better while supporting each other through challenges.
                </p>
                <p>
                  Whether you're a strategist, creative, or technologist, you'll find a home at RADIUS where your expertise is valued and your growth is prioritized.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.pexels.com/photos/8117420/pexels-photo-8117420.jpeg" 
                alt="Team collaboration"
                className="w-full rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">
            Stay in Touch
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Don't see the right role? We're always looking for exceptional talent. Send us your portfolio and let's talk.
          </p>
          <Button 
            onClick={() => toast.success('Thank you! We\'ll review your application and be in touch soon.')}
            size="lg" 
            className="btn-primary group"
          >
            Send General Application
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Careers;
